import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface YearlyMetric {
  year: number;
  totalDownloads: number;
  premiumUsers: number;
  multiCityPurchases: number;
  ticketPurchases: number;
  revenueFromPremium: number;
  revenueFromMultiCity: number;
  revenueFromTickets: number;
  totalRevenue: number;
  totalCAC: number;
  operationalCosts: number;
  contentCosts: number;
  totalCosts: number;
  netRevenue: number;
  averageRevenuePerUser: number;
  netRevenuePerUser: number;
  requiredPointsOfSale: number;
}

interface Params {
  downloads: Record<number, number>;
  conversionRate: number;
  premiumPrice: number;
  multiCityPurchaseRate: number;
  multiCityPrice: number;
  ticketPurchaseRate: number;
  ticketCommission: number;
  partnerRevenueShare: number;
  monthlyOperationalCosts: number;
  contentCostPerCity: number;
  newCitiesPerQuarter: number;
  cagr: number;
}

const FinancialModel = () => {
  const [years] = useState<number[]>([2024, 2025, 2026, 2027]);
  const [params, setParams] = useState<Params>({
    downloads: {
      2024: 20000,
      2025: 100000,
      2026: 500000,
      2027: 1000000
    },
    conversionRate: 0.1,
    premiumPrice: 4.99,
    multiCityPurchaseRate: 0.05,
    multiCityPrice: 4.99,
    ticketPurchaseRate: 0.1,
    ticketCommission: 1.5,
    partnerRevenueShare: 0.5,
    monthlyOperationalCosts: 3000,
    contentCostPerCity: 100,
    newCitiesPerQuarter: 1,
    cagr: 300
  });

  const [yearlyMetrics, setYearlyMetrics] = useState<YearlyMetric[]>([]);

  const calculateCAGR = (startValue: number, endValue: number, years: number): number => {
    return ((Math.pow(endValue / startValue, 1 / years) - 1) * 100);
  };

  const calculateFutureValue = (startValue: number, cagr: number, years: number): number => {
    return startValue * Math.pow(1 + cagr / 100, years);
  };

  useEffect(() => {
    const calculateMetrics = (): YearlyMetric[] => {
      return years.map(year => {
        const totalDownloads = params.downloads[year];
        const premiumUsers = totalDownloads * params.conversionRate;
        const multiCityPurchases = premiumUsers * params.multiCityPurchaseRate;
        const ticketPurchases = premiumUsers * params.ticketPurchaseRate;
        const requiredPointsOfSale = Math.ceil(premiumUsers / 200);

        const calculatedCAC = params.premiumPrice * params.partnerRevenueShare;

        const revenueFromPremium = premiumUsers * params.premiumPrice;
        const revenueFromMultiCity = multiCityPurchases * params.multiCityPrice;
        const revenueFromTickets = ticketPurchases * params.ticketCommission;

        const totalRevenue = revenueFromPremium + revenueFromMultiCity + revenueFromTickets;
        const totalCAC = premiumUsers * calculatedCAC;
        const operationalCosts = params.monthlyOperationalCosts * 12;
        const contentCosts = params.contentCostPerCity * params.newCitiesPerQuarter * 4;
        const totalCosts = totalCAC + operationalCosts + contentCosts;

        const netRevenue = totalRevenue - totalCosts;
        const averageRevenuePerUser = totalRevenue / premiumUsers || 0;
        const netRevenuePerUser = (totalRevenue - totalCAC) / premiumUsers || 0;

        return {
          year,
          totalDownloads,
          premiumUsers,
          multiCityPurchases,
          ticketPurchases,
          revenueFromPremium,
          revenueFromMultiCity,
          revenueFromTickets,
          totalRevenue,
          totalCAC,
          operationalCosts,
          contentCosts,
          totalCosts,
          netRevenue,
          averageRevenuePerUser,
          netRevenuePerUser,
          requiredPointsOfSale
        };
      });
    };

    setYearlyMetrics(calculateMetrics());
  }, [params, years]);

  const handleParamChange = (paramName: string, value: string | number) => {
    setParams(prev => {
      if (paramName.startsWith('downloads.')) {
        const year = parseInt(paramName.split('.')[1]);
        const newDownloads = { ...prev.downloads, [year]: parseFloat(value.toString()) || 0 };
        
        const firstYear = Math.min(...years);
        const lastYear = Math.max(...years);
        const newCAGR = calculateCAGR(
          newDownloads[firstYear],
          newDownloads[lastYear],
          lastYear - firstYear
        );

        return {
          ...prev,
          downloads: newDownloads,
          cagr: parseFloat(newCAGR.toFixed(1))
        };
      }

      if (paramName === 'cagr') {
        const newCAGR = parseFloat(value.toString()) || 0;
        const firstYear = Math.min(...years);
        const startValue = prev.downloads[firstYear];
        
        const newDownloads = { ...prev.downloads };
        years.forEach(year => {
          if (year > firstYear) {
            newDownloads[year] = Math.round(
              calculateFutureValue(startValue, newCAGR, year - firstYear)
            );
          }
        });

        return {
          ...prev,
          downloads: newDownloads,
          cagr: newCAGR
        };
      }

      return {
        ...prev,
        [paramName]: parseFloat(value.toString()) || 0
      };
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-EU', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-EU', { 
      maximumFractionDigits: 0 
    }).format(value);
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-6">PurrWalk Financial Model</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Parameters */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Growth Targets</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CAGR (%)</Label>
                  <Input 
                    type="number"
                    value={params.cagr}
                    onChange={(e) => handleParamChange('cagr', e.target.value)}
                  />
                </div>
                {years.map(year => (
                  <div key={year}>
                    <Label>{year} Downloads Target</Label>
                    <Input 
                      type="number"
                      value={params.downloads[year]}
                      onChange={(e) => handleParamChange(`downloads.${year}`, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Core Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Premium Conversion Rate (%)</Label>
                  <Input 
                    type="number"
                    value={params.conversionRate * 100}
                    onChange={(e) => handleParamChange('conversionRate', parseFloat(e.target.value) / 100)}
                  />
                </div>
                <div>
                  <Label>Premium Price (€)</Label>
                  <Input 
                    type="number"
                    value={params.premiumPrice}
                    onChange={(e) => handleParamChange('premiumPrice', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Multi-City Purchase Rate (%)</Label>
                  <Input 
                    type="number"
                    value={params.multiCityPurchaseRate * 100}
                    onChange={(e) => handleParamChange('multiCityPurchaseRate', parseFloat(e.target.value) / 100)}
                  />
                </div>
                <div>
                  <Label>Multi-City Revenue (€)</Label>
                  <Input 
                    type="number"
                    value={params.multiCityPrice}
                    onChange={(e) => handleParamChange('multiCityPrice', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Ticket Purchase Rate (%)</Label>
                  <Input 
                    type="number"
                    value={params.ticketPurchaseRate * 100}
                    onChange={(e) => handleParamChange('ticketPurchaseRate', parseFloat(e.target.value) / 100)}
                  />
                </div>
                <div>
                  <Label>Ticket Commission (€)</Label>
                  <Input 
                    type="number"
                    value={params.ticketCommission}
                    onChange={(e) => handleParamChange('ticketCommission', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Operational Parameters</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monthly Operational Costs (€)</Label>
                  <Input 
                    type="number"
                    value={params.monthlyOperationalCosts}
                    onChange={(e) => handleParamChange('monthlyOperationalCosts', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Content Cost per City (€)</Label>
                  <Input 
                    type="number"
                    value={params.contentCostPerCity}
                    onChange={(e) => handleParamChange('contentCostPerCity', e.target.value)}
                  />
                </div>
                <div>
                  <Label>New Cities per Quarter</Label>
                  <Input 
                    type="number"
                    value={params.newCitiesPerQuarter}
                    onChange={(e) => handleParamChange('newCitiesPerQuarter', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Partner Revenue Share (%)</Label>
                  <Input 
                    type="number"
                    value={params.partnerRevenueShare * 100}
                    onChange={(e) => handleParamChange('partnerRevenueShare', parseFloat(e.target.value) / 100)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2 text-right">Downloads</th>
                    <th className="p-2 text-right">Premium Users</th>
                    <th className="p-2 text-right">Points of Sale</th>
                    <th className="p-2 text-right">Revenue</th>
                    <th className="p-2 text-right">Costs</th>
                    <th className="p-2 text-right">Net Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyMetrics.map((data) => (
                    <tr key={data.year} className="border-t">
                      <td className="p-2">{data.year}</td>
                      <td className="p-2 text-right">{formatNumber(data.totalDownloads)}</td>
                      <td className="p-2 text-right">{formatNumber(data.premiumUsers)}</td>
                      <td className="p-2 text-right">{formatNumber(data.requiredPointsOfSale)}</td>
                      <td className="p-2 text-right">{formatCurrency(data.totalRevenue)}</td>
                      <td className="p-2 text-right">{formatCurrency(data.totalCosts)}</td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {formatCurrency(data.netRevenue)}
                          {data.netRevenue > 0 ? (
                            <TrendingUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detailed First Year Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">Revenue Breakdown (Year 1)</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Premium Subscriptions:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.revenueFromPremium || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Multi-City Revenue:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.revenueFromMultiCity || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ticket Commission:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.revenueFromTickets || 0)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Total Revenue:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.totalRevenue || 0)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Cost Breakdown (Year 1)</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Customer Acquisition:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.totalCAC || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Operational Costs:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.operationalCosts || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Content Costs:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.contentCosts || 0)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Total Costs:</span>
                    <span>{formatCurrency(yearlyMetrics[0]?.totalCosts || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="text-sm text-gray-500">Average Revenue per User</div>
                <div className="text-2xl font-bold">
                  {formatCurrency(yearlyMetrics[0]?.averageRevenuePerUser || 0)}
                </div>
                <div className="text-xs text-gray-400">
                  Premium + Multi-City + Tickets
                </div>
              </Card>

              <Card className="p-4">
                <div className="text-sm text-gray-500">Net Revenue per User</div>
                <div className="text-2xl font-bold">
                  {formatCurrency(yearlyMetrics[0]?.netRevenuePerUser || 0)}
                </div>
                <div className="text-xs text-gray-400">
                  After acquisition costs
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FinancialModel;